/**
 * WAF bot management / L7 DDoS countermeasures (Owl/Mlytics — DDoSService +
 * BotsService). Service-scoped and domain-anchored (`?domain=`).
 *
 * Backed by `modules/service/waf/controller_ddos.go` + `controller_bots.go`:
 *   - GET   /services/:service_id/waf/ddos  → a domain's DDoS/bot settings
 *   - PATCH /services/:service_id/waf/ddos  → update settings (partial; nested objects sent whole)
 *   - GET   /services/:service_id/waf/bots  → bot catalog (user-agent tokens) for a category
 *
 * The nested `browser_check` / `security` objects are replaced as a whole on
 * PATCH (their inner fields are non-pointer on the BE), so callers must merge a
 * changed field with the current values before sending.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3, wafKeys, unwrap } from './_shared';

/** "Browser integrity check" — enable + challenge level. */
export interface WafDDoSBrowserCheck {
  enable: boolean;
  mode?: string; // "standard" | "strict"
}

/** "Security level" / bot-management challenge config. */
export interface WafDDoSSecurity {
  enable: boolean;
  level?: string; // "underattack" | "high" | "medium" | "low" | "essentiallyoff"
  mode?: string; // "javascript" | "javascript-nodelay" | "human"
  ttl?: number; // challenge passage, seconds
}

/** Full DDoS/bot settings for a domain (`GET /waf/ddos`). */
export interface WafDDoSSettings {
  seo_enable: boolean; // allow search-engine crawler bypass
  block_security_scan: boolean; // block security/vulnerability scanners
  block_bad_bot: boolean; // block rogue crawlers and bad bots
  browser_check: WafDDoSBrowserCheck;
  security: WafDDoSSecurity;
}

/** Partial update body (`PATCH /waf/ddos`). Nested objects are sent whole. */
export interface WafDDoSSettingsUpdate {
  seo_enable?: boolean;
  block_security_scan?: boolean;
  block_bad_bot?: boolean;
  browser_check?: WafDDoSBrowserCheck;
  security?: WafDDoSSecurity;
}

/** One bot catalog — the user-agent tokens a category matches (`GET /waf/bots`). */
export interface WafBotCatalog {
  category: string; // "spiders" | "scan_bot" | "bad_bot"
  entries: string[];
}

/** Read a domain's DDoS/bot settings. */
export function useWafDDoSSettings(serviceId: string | undefined, domain: string | undefined) {
  return useQuery<WafDDoSSettings>({
    queryKey: wafKeys.ddos(serviceId ?? '', domain ?? ''),
    enabled: !!serviceId && !!domain,
    queryFn: async () =>
      unwrap<WafDDoSSettings>(
        await serviceApiV3.get(`/services/${serviceId}/waf/ddos`, { params: { domain } }),
      ),
  });
}

/** Update a domain's DDoS/bot settings (partial). */
export function useUpdateWafDDoSSettings(
  serviceId: string | undefined,
  domain: string | undefined,
  options?: UseMutationOptions<WafDDoSSettings, Error, WafDDoSSettingsUpdate>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: WafDDoSSettingsUpdate) =>
      unwrap<WafDDoSSettings>(
        await serviceApiV3.patch(`/services/${serviceId}/waf/ddos`, patch, { params: { domain } }),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId && domain) qc.invalidateQueries({ queryKey: wafKeys.ddos(serviceId, domain) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Read a bot catalog by category (lazy — enable when a category is set). */
export function useWafBotCatalog(serviceId: string | undefined, category: string | undefined) {
  return useQuery<WafBotCatalog>({
    queryKey: wafKeys.botCatalog(serviceId ?? '', category ?? ''),
    enabled: !!serviceId && !!category,
    staleTime: 30 * 60_000,
    queryFn: async () =>
      unwrap<WafBotCatalog>(
        await serviceApiV3.get(`/services/${serviceId}/waf/bots`, { params: { category } }),
      ),
  });
}
